import {
  DESTINATION_APP_TYPE,
  MessageConfig,
  MessageData,
  MessagePayload,
  ValidMessageType,
  MessagePayloadMapByMessageTypeUnion,
} from './messages.model';

type AddOnMessageActionAlt = <
  const D extends DESTINATION_APP_TYPE,
  const M extends ValidMessageType<D>
>(
  destinationApp: D,
  messageType: M,
  action: (payload: MessagePayload<M>, messageEvent: MessageEvent<{
    destinationApp: D,
    messageType: M,
    payload: MessagePayload<M> }>) => Promise<void>
) => void;

type PostWindowMessage = <
  const T extends DESTINATION_APP_TYPE,
  const M extends ValidMessageType<T>
>(
  destinationApp: T,
  messageType: M,
  payload: MessagePayload<M>
) => void;

export interface UseMessages {
  addOnMessageActionAlt: AddOnMessageActionAlt;
  postWindowMessage: PostWindowMessage;
  removeMessageListener: () => void;
  startMessageListener: () => void;
}

export const useMessages: () => UseMessages = () => {
  const messageActions: MessageConfig[] = [];

  const onMessage = (event: MessageEvent<MessageData>) => {
    if (!event.data) {
      return;
    }
    const messageData: MessageData = event.data;
    const messageType = messageData.messageType;
    const eventDestinationApp: DESTINATION_APP_TYPE = messageData.destinationApp;

    messageActions.forEach(async (messageConfig) => {
      // Filtering for messageType
      if (messageType !== messageConfig.messageType) {
        return;
      }
      // Filtering for message destination app
      if(eventDestinationApp !== messageConfig.destinationApp) {
        return;
      }

      await messageConfig.action(messageData.payload, event);
    });
  };

  const addOnMessageActionAlt: AddOnMessageActionAlt = (destinationApp, messageType, action) => {
    messageActions.push({
      messageType,
      destinationApp,
      action: action as (payload: MessagePayloadMapByMessageTypeUnion) => Promise<void>,
    });
  };

  const postWindowMessage: PostWindowMessage = (destinationApp, messageType, payload) => {
    window.postMessage({
      destinationApp,
      messageType,
      payload,
    }, window.location.origin);
  };

  const startMessageListener = () => {
    window.addEventListener('message', onMessage);
  };

  const removeMessageListener = () => {
    window.removeEventListener('message', onMessage);
  };

  return {
    removeMessageListener,
    startMessageListener,
    addOnMessageActionAlt,
    postWindowMessage,
  };
};
